import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingState from '../../interfaces/IMappingState'
import { SearchRequest, MappingStateSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingStateList, mappingStateSave } from '../../services/api'

interface MappingStateContextState extends BaseContextState {
  mappingStates: Array<IMappingState> | null,
  mappingState: IMappingState | null
}

interface MappingStateContextData extends MappingStateContextState {
  resetErrors: Function,
  requestMappingStateList: (args: SearchRequest) => Promise<any>,
  requestMappingStateFind: (args: SearchRequest) => Promise<any>,
  requestMappingStateSave: (args: MappingStateSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingStateContextData)

// Export consumer
export const MappingStateConsumer = Consumer

export class MappingStateProvider extends BaseContextProvider {
  state: MappingStateContextState = {
    mappingStates: null,
    mappingState: null
  }

  /**
   * Make api request for mapping state list
   *
   * @param {SearchRequest} payload
   */
  requestMappingStateList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingStateList(payload)

        // Process our response
        this.processResponse(response, {
          paginate: {
            destination: 'mappingStates'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping state finder
   *
   * @param {SearchRequest} payload
   */
  requestMappingStateFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingState: null
      }, async () => {
        payload.options = {
          ...payload.options,
          _single: true
        }
        payload.search = {
          ...payload.search,
          state: {
            with: {
              _fields: ["id", "uuid", "name", "country_id"],
              country: {
                with: {
                  _fields: ["id", "uuid", "name"],
                }
              }
            }
          },
          shelter: {
            with: {
              _fields: ["id", "uuid", "name"]
            }
          }
        }

        const response = await mappingStateList(payload)

        // Process our response
        this.processResponse(response, {
          mappings: {
            destination: 'mappingState'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping state save
   *
   * @param {MappingStateSaveRequest} payload
   */
  requestMappingStateSave = (payload: MappingStateSaveRequest) => {
    // No need to null `mappingState` on save: the form stays mounted (its render
    // no longer depends on this value) and re-fetches fresh whenever the modal reopens.
    return new Promise<void>((resolve, reject) => this.setState({
      fetching: true,
      error: null,
      // mappingState: null
    }, async () => {
      try {
        const response = await mappingStateSave(payload)

        // Process our response
        const result = await this.processResponse(response, {
          mappings: [
            { origin: 'mapping_state', destination: 'mappingState' }
          ]
        })

        // throw if error happend
        if (result.error) {
          return reject(new Error(result.error))
        }
      } finally {
        resolve()
      }
    }))
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingStateList: this.requestMappingStateList,
          requestMappingStateFind: this.requestMappingStateFind,
          requestMappingStateSave: this.requestMappingStateSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
